import { create } from 'apisauce'
import { ColorSchemeName } from 'react-native'

import Config from 'APP/Config'
import { type CmsSlug } from 'APP/Config/types'
import type {
  GetExternalServiceParams,
  AppComponentsParams,
  GetAppComponentsParams,
  VideoSearchParams,
  AudioSearchParams,
  DocumentSearchParams,
  AppLayoutParams,
} from 'APP/Services/CMS/index.types'
import type {
  DocumentList,
  Documents,
  Document,
  VideoList,
  AudioList,
  Habit,
  AppLayout,
  Profile,
  AppContent,
  AppExternalService,
  AppServiceGroup,
  AppComponentList,
  AppServiceFeatureList,
  SupportedLocale,
} from '@dialogue/cms'
import I18n from 'APP/I18n'

const api = create({
  baseURL: Config.CMS.baseUrl,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 50000, // 50 seconds
})
Config.onConfigKeyChange('CMS', (done) => {
  api.setBaseURL(Config.CMS.baseUrl)
  done(null)
})
// Parse filters into a single query string
const queryParams = (
  filters: DocumentSearchParams | VideoSearchParams | AudioSearchParams | AppLayoutParams
): string =>
  Object.entries(filters)
    .reduce((query, [key, value]) => {
      // Implement `repeat` array format (e.g. `?eg=a&eg=b`), because FastAPI
      // doesn't support default axios format (e.g. `?eg[]=a,b`)
      if (Array.isArray(value)) {
        value.forEach((v) => query.append(key, v))
      } else {
        query.append(key, value)
      }
      return query
    }, new URLSearchParams())
    .toString()

export async function getArticles(
  ctaId: CmsSlug | null = 'mentalHealth',
  bySearch = false,
  filters: DocumentSearchParams = {},
  locale: SupportedLocale | null = I18n.baseLocale
): Promise<DocumentList | Documents | undefined> {
  if (bySearch) {
    const response = await api
      .get<Documents>(`/document/search/?${queryParams(filters)}`, { locale })
      .then((response) => {
        if (response.ok) {
          return response.data
        } else {
          return {}
        }
      })

    return response
  }

  const slugs = Config.CMS.slugs
  const slugId = slugs && ctaId && slugs[ctaId]

  const response = await api
    .get<DocumentList>(`/document-list/by-slug/${slugId}`, { locale })
    .then((response) => {
      if (response.ok) {
        return response.data
      } else {
        throw new Error(`Error retrieving articles`)
      }
    })

  return response
}

export async function getVideos(
  filters: VideoSearchParams = {},
  locale: SupportedLocale | null = I18n.baseLocale
): Promise<VideoList | undefined> {
  const response = await api
    .get<VideoList>(`/video/search/?${queryParams(filters)}`, { locale })
    .then((response) => {
      if (response.ok) {
        return response.data
      } else {
        return {}
      }
    })

  return response
}

export async function getAudio(
  filters: AudioSearchParams = {},
  locale: SupportedLocale | null = I18n.baseLocale
): Promise<AudioList | undefined> {
  const response = await api
    .get<AudioList>(`/audio/search/?${queryParams(filters)}`, { locale })
    .then((response) => {
      if (response.ok) {
        return response.data
      } else {
        return {}
      }
    })

  return response
}

export async function getHabit(
  id: string,
  locale: SupportedLocale | null = I18n.baseLocale
): Promise<Habit | undefined | object> {
  const response = await api.get<Habit>(`/habit/${id}`, { locale }).then((response) => {
    if (response.ok) {
      return response.data
    } else {
      return {}
    }
  })

  return response
}

export async function getLayout({
  contentTags = [],
  locale = I18n.baseLocale,
}: AppLayoutParams): Promise<AppLayout | undefined> {
  const response = await api
    .get<AppLayout>(`/app_layout?${queryParams({ tags: contentTags, locale })}`)
    .then((response) => {
      if (response.ok) {
        return response.data
      } else {
        throw {
          status: response.status,
          problem: response.problem,
          originalError: response.originalError,
          data: response.data,
        }
      }
    })

  return response
}

export async function getServiceFeatures(): Promise<AppServiceFeatureList | undefined> {
  const response = await api
    .get<AppServiceFeatureList>(`/app_service_features`)
    .then((response) => {
      if (response.ok) {
        return response.data
      } else {
        throw {
          status: response.status,
          problem: response.problem,
          originalError: response.originalError,
          data: response.data,
        }
      }
    })

  return response
}

export async function getCmsFeed(
  entryId: string,
  locale = I18n.baseLocale
): Promise<AppContent | undefined> {
  const response = await api
    .get<AppContent>(`/app_content/${entryId}`, { locale })
    .then((response) => {
      if (response.ok) {
        return response.data
      } else {
        throw {
          status: response.status,
          problem: response.problem,
          originalError: response.originalError,
          data: response.data,
        }
      }
    })

  return response
}

export async function getBio(
  entryId: string | number,
  locale = I18n.baseLocale,
  memberId?: string
): Promise<Profile | undefined> {
  const response = await api.get<Profile>(`/profile/${entryId}`, {
    locale,
    member_id: memberId,
  })
  if (response.ok && response.data) {
    return response.data
  }

  throw {
    status: response.status,
    problem: response.problem,
    originalError: response.originalError,
    data: response.data,
  }
}

export async function getCmsContent(
  path: string,
  style: ColorSchemeName = 'light',
  toJson = true,
  locale = I18n.baseLocale
): Promise<Document> {
  const response = await api.get<Document>(path, { style, to_json: toJson, locale })
  if (response.ok && response.data) {
    return response.data
  }

  throw {
    status: response.status,
    problem: response.problem,
    originalError: response.originalError,
    data: response.data,
  }
}

export async function getAppComponents({
  locale = I18n.baseLocale,
  brandId = Config.BRAND_ID,
  mobileComponentName,
}: GetAppComponentsParams): Promise<AppComponentList | undefined> {
  let params: AppComponentsParams = { locale, brand_id: brandId }

  // Add mobile_component_name to params, if set
  if (mobileComponentName) {
    params['mobile_component_name'] = mobileComponentName
  }

  const response = await api.get<AppComponentList>(`/app_components?`, params)
  if (response.ok) {
    return response.data
  } else {
    throw {
      status: response.status,
      problem: response.problem,
      originalError: response.originalError,
      data: response.data,
    }
  }
}

export async function getOneServiceGroup(
  id: string,
  locale = I18n.baseLocale
): Promise<AppServiceGroup | undefined> {
  const response = await api.get<AppServiceGroup>(`/app_service_groups/${id}`, { locale })
  if (response.ok) {
    return response.data
  } else {
    throw {
      status: response.status,
      problem: response.problem,
      originalError: response.originalError,
      data: response.data,
    }
  }
}

export async function getExternalService(
  id: string,
  params: GetExternalServiceParams
): Promise<AppExternalService | undefined> {
  const response = await api.get<AppExternalService>(`/app_external_services/${id}`, params)
  if (response.ok) {
    return response.data
  } else {
    throw {
      status: response.status,
      problem: response.problem,
      originalError: response.originalError,
      data: response.data,
    }
  }
}

export default {
  getArticles,
  getVideos,
  getAudio,
  getHabit,
  getLayout,
  getCmsFeed,
  getBio,
  getCmsContent,
  getAppComponents,
  getOneServiceGroup,
  getExternalService,
}
